import { Divider, Flex, Typography } from 'antd';
import classNames from 'classnames';
import { memo, useMemo } from 'react';

import styles from './styles.module.scss';
import { buildMarkerList, clampPercent, metricDisplayValue } from './utils';

import type { TMetricItemProps } from './types';

const { Text } = Typography;

const variantClassMap: Record<string, string> = {
  efficiency: styles['variant-efficiency'],
  effectiveness: styles['variant-effectiveness'],
  quality: styles['variant-quality'],
  custom: styles['variant-custom'],
};

const helperToneClassMap: Record<string, string> = {
  default: styles['tone-default'],
  positive: styles['tone-positive'],
  warning: styles['tone-warning'],
  negative: styles['tone-negative'],
  accent: styles['tone-accent'],
};

const MetricItem = memo(({ metric, showDivider }: TMetricItemProps) => {
  const {
    label,
    percent,
    summaryLabel,
    targetLabel,
    helperText,
    helperTone = 'default',
    variant = 'custom',
    targetPercent,
    markers,
  } = metric;

  const resolvedPercent = clampPercent(percent);
  const resolvedValue = metricDisplayValue(metric);

  const markerList = useMemo(
    () => buildMarkerList(markers, targetPercent, targetLabel),
    [markers, targetPercent, targetLabel],
  );

  return (
    <div
      className={classNames(styles['metric-group'], variantClassMap[variant])}
    >
      <Flex
        justify="space-between"
        align="center"
        className={styles['metric-row']}
      >
        <Text className={styles['metric-label']}>{label}</Text>
        <Text className={styles['metric-value']}>{resolvedValue}</Text>
      </Flex>

      <div className={styles['progress-block']}>
        <div
          role="progressbar"
          aria-valuenow={resolvedPercent}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuetext={`${label} ${resolvedValue}`}
          className={styles['progress-track']}
        >
          <div
            className={styles['progress-fill']}
            style={{ width: `${resolvedPercent}%` }}
          />
          {markerList.map((marker, index) => (
            <span
              key={`${marker.percent}-${index}`}
              className={styles['progress-marker']}
              data-emphasis={marker.emphasis ?? 'regular'}
              style={{ left: `${clampPercent(marker.percent)}%` }}
              aria-hidden={marker.label ? undefined : true}
              aria-label={marker.label}
            />
          ))}
        </div>
        <div className={styles['progress-meta']}>
          <Text type="secondary" className={styles['summary-label']}>
            {summaryLabel}
          </Text>
          <Text type="secondary" className={styles['summary-label']}>
            {targetLabel}
          </Text>
        </div>
        {helperText ? (
          <Text
            className={classNames(
              styles['helper-text'],
              helperToneClassMap[helperTone],
            )}
          >
            {helperText}
          </Text>
        ) : null}
      </div>

      {showDivider && <Divider className={styles['metric-divider']} />}
    </div>
  );
});

MetricItem.displayName = 'ProductivityMetricItem';

export default MetricItem;
